
import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Alert() {
  const [email, setEmail] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const questions = [
    "What if help was just one touch away?",
    "Why should distance determine your safety?",
    "How fast can life change in an emergency?",
    "What if your phone could save your life?"
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % questions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: "ri-alarm-warning-line",
      title: "Lightning-Fast Alerts",
      description: "Instant notifications that reach your emergency contacts in milliseconds when you need help most.",
      intensity: "Life-Saving",
      color: "from-red-500 to-orange-600"
    },
    {
      icon: "ri-map-pin-line",
      title: "Precision Location Mastery",
      description: "Automatically shares your exact location with emergency responders and contacts with pinpoint accuracy.",
      intensity: "Game-Changing",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: "ri-phone-line",
      title: "Omni-Channel Communication",
      description: "Alerts sent via SMS, in-app notifications, and direct calls for maximum reach and reliability.",
      intensity: "Revolutionary",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: "ri-shield-check-line",
      title: "Fort Knox Security",
      description: "Your data is encrypted with military-grade security and only shared during actual emergencies.",
      intensity: "Bulletproof",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: "ri-time-line",
      title: "Never-Sleep Monitoring",
      description: "Round-the-clock monitoring ensures help is always available when needed, 365 days a year.",
      intensity: "Relentless",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: "ri-group-line",
      title: "Emergency Superhighway",
      description: "Connect with family, friends, and local emergency services instantly through our powerful network.",
      intensity: "Mind-Bending",
      color: "from-orange-500 to-red-600"
    }
  ];

  const useCases = [
    {
      title: "Personal Safety Revolution",
      description: "Walking alone at night, traveling to unfamiliar areas, or feeling unsafe - never be vulnerable again",
      icon: "ri-user-line",
      stats: "95% faster response time"
    },
    {
      title: "Medical Emergency Mastery",
      description: "Sudden health issues, accidents, or medical conditions requiring immediate help - every second counts",
      icon: "ri-heart-pulse-line",
      stats: "300% better outcomes"
    },
    {
      title: "Vehicle Breakdown Domination",
      description: "Car troubles, accidents, or being stranded in remote locations - help finds you instantly",
      icon: "ri-car-line",
      stats: "50% faster rescue time"
    },
    {
      title: "Home Security Fortress",
      description: "Break-ins, suspicious activity, or feeling threatened at home - your sanctuary protected",
      icon: "ri-home-line",
      stats: "99% threat prevention"
    }
  ];

  const handleBetaSignup = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle beta signup
    console.log('Beta signup:', email);
    setEmail('');
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Alert - Revolutionary Emergency Response App",
    "description": "Your emergency response revolution - lightning-fast, bulletproof, life-saving. Real-time distress alerts, precision location sharing, omni-channel communication.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/alert`,
    "applicationCategory": "Safety",
    "operatingSystem": "iOS, Android",
    "author": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "NGN"
    },
    "featureList": [
      "Lightning-fast distress alerts",
      "Precision location sharing",
      "Omni-channel communication",
      "Never-sleep monitoring",
      "Emergency superhighway network"
    ],
    "screenshot": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/alert-screenshot.jpg`,
    "softwareVersion": "1.0",
    "datePublished": "2024",
    "inLanguage": "en"
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-hidden">
      <SEO
        title="Alert - Revolutionary Emergency Response App | TheSpire"
        description="Your emergency response revolution - lightning-fast, bulletproof, life-saving. Real-time distress alerts, precision location sharing, omni-channel communication."
        keywords="emergency app revolution, safety app, distress alert, location sharing, emergency response, personal safety, nigeria safety, life-saving technology"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 25%, transparent 50%)`
        }}
      />
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-red-500 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-transparent to-[#6D03F4]/20" />
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Rotating Questions */}
              <div className="mb-8 h-16 flex items-center">
                <p className="text-lg md:text-xl text-red-400 font-medium transition-all duration-1000 transform">
                  {questions[currentQuestion]}
                </p>
              </div>

              <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
                <span className="block text-white">YOUR</span>
                <span className="block bg-gradient-to-r from-red-500 to-[#6D03F4] bg-clip-text text-transparent">
                  LIFELINE
                </span>
              </h1>
              
              <p className="text-xl md:text-3xl text-gray-300 mb-12 font-light">
                <span className="text-red-400 font-bold">Lightning-fast</span>, bulletproof, life-saving. 
                Alert connects you to help when <span className="text-[#6D03F4] font-bold">every second counts</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-6">
                <Button size="lg" className="bg-gradient-to-r from-red-500 to-[#6D03F4] hover:from-red-600 hover:to-[#5A02D1] text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
                  JOIN THE REVOLUTION
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  DISCOVER POWER
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-[#6D03F4] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <i className="ri-alarm-warning-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-2">ALERT</h3>
                  <p className="text-gray-300 text-lg">Emergency Response Revolution</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 rounded-lg p-4 flex items-center space-x-3 border border-green-500/30">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white font-semibold">Real-time monitoring ACTIVE</span>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500/20 to-indigo-600/20 rounded-lg p-4 flex items-center space-x-3 border border-blue-500/30">
                    <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                    <span className="text-white font-semibold">5 emergency contacts CONNECTED</span>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-600/20 rounded-lg p-4 flex items-center space-x-3 border border-purple-500/30">
                    <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
                    <span className="text-white font-semibold">Location services ENABLED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(239, 68, 68, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(239, 68, 68, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'grid-move 20s linear infinite'
          }} />
        </div>
      </section>

      {/* Revolutionary Features */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              REVOLUTIONARY
              <span className="block bg-gradient-to-r from-red-500 to-[#6D03F4] bg-clip-text text-transparent">
                FEATURES
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Alert combines <span className="text-red-400 font-bold">cutting-edge technology</span> with intuitive design to keep you safe and connected.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:border-red-500/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-red-500 to-[#6D03F4] text-white">
                    {feature.intensity}
                  </span>
                </div>
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-[#6D03F4]/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Use Cases */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              WHEN YOU NEED
              <span className="block bg-gradient-to-r from-red-500 to-[#6D03F4] bg-clip-text text-transparent">
                ALERT MOST
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300">
              Real situations where Alert becomes your <span className="text-red-400 font-bold">ultimate lifeline</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:border-red-500/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-[#6D03F4] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${useCase.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-3">{useCase.title}</h3>
                    <p className="text-gray-300 mb-4">{useCase.description}</p>
                    <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                      {useCase.stats}
                    </div>
                  </div>
                </div>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-[#6D03F4]/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Revolution */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              HOW ALERT
              <span className="block bg-gradient-to-r from-red-500 to-[#6D03F4] bg-clip-text text-transparent">
                REVOLUTIONIZES
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300">
              <span className="text-red-400 font-bold">Lightning-fast</span>, bulletproof, life-saving emergency response in three revolutionary steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-red-500 to-[#6D03F4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl font-black text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">ACTIVATE REVOLUTION</h3>
              <p className="text-gray-300 text-lg">Press the emergency button or use voice activation to trigger an alert in milliseconds.</p>
            </div>
            
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-red-500 to-[#6D03F4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl font-black text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">PRECISION LOCATION</h3>
              <p className="text-gray-300 text-lg">Your exact location is automatically shared with emergency contacts and services with pinpoint accuracy.</p>
            </div>
            
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-red-500 to-[#6D03F4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl font-black text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">HELP DOMINATES</h3>
              <p className="text-gray-300 text-lg">Emergency contacts and services are notified instantly to provide immediate assistance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Revolutionary Beta Signup */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-[#6D03F4] opacity-20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            BE THE FIRST TO
            <span className="block bg-gradient-to-r from-red-500 to-[#6D03F4] bg-clip-text text-transparent">
              EXPERIENCE ALERT
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12">
            Join our beta program and help us build the <span className="text-red-400 font-bold">future of emergency response</span>.
          </p>
          
          <form onSubmit={handleBetaSignup} className="max-w-md mx-auto">
            <div className="flex gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 text-lg"
                required
              />
              <a 
                href="https://chat.whatsapp.com/F82KXNs9f69C1zBJ6eoJrB" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-red-500 to-[#6D03F4] hover:from-red-600 hover:to-[#5A02D1] text-white px-8 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap rounded-lg inline-block text-center cursor-pointer"
              >
                JOIN REVOLUTION
              </a>
            </div>
          </form>
          
          <p className="text-gray-400 text-sm mt-6">
            No spam. We'll only contact you with <span className="text-red-400 font-bold">revolutionary updates</span> about Alert.
          </p>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 group"
        >
          <i className="ri-whatsapp-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>

      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </div>
  );
}
